/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Subject, firstValueFrom, tap } from 'rxjs';
import { AuthorizationModel, RefreshTokenResponseModel, SsoProvider, User } from '../models/auth.model';
import { EnvironmentConfig } from '../models/config.model';
import { DealerChangeModel } from '../models/framework.model';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);

  private config: EnvironmentConfig;
  static REFRESH_TOKEN_BEFORE_EXPIRATION_PCT = 0.1; // 0.1 = 6 mins before expiration (1 hour default token expiration)
  static FORCE_BUILD_INCREMENTER = 22; // increment to force a build of all ACADV apps

  private AUTH_STORAGE_KEY = 'authToken';
  private EXPIRY_STORAGE_KEY = 'expiry';
  private USER_KEY = 'user';
  private REDIRECT_STORAGE_KEY = 'redirectTo';
  private SSO_PROVIDER_STORAGE_KEY = 'ssoProvider';
  private REFRESH_STORAGE_KEY = 'refreshToken';
  private AUTH_COGNITO_KEY = 'cogToken';

  private _token$ = new BehaviorSubject<string | undefined>(undefined);
  private _cognitoToken$ = new BehaviorSubject<string | undefined>(undefined);
  private _refreshToken$ = new BehaviorSubject<string | undefined>(undefined);
  private _expiry$ = new BehaviorSubject<number | undefined>(undefined);
  private _user$ = new BehaviorSubject<User | undefined>(undefined);
  private _beforeSignOut$ = new Subject<boolean>();
  private _connectionId?: string;

  user$ = this._user$.asObservable();
  token$ = this._token$.asObservable();
  expiry$ = this._expiry$.asObservable();
  cognitoToken$ = this._cognitoToken$.asObservable();
  refreshToken$ = this._refreshToken$.asObservable();
  dealerChanged$ = new Subject<DealerChangeModel>();
  beforeSignOut$ = this._beforeSignOut$.asObservable();

  refreshTokenTimer?: any;
  isFromLocal = window.location.href.indexOf('localhost') >= 0;
  isFromParallel = window.location.href.indexOf('acadv.') >= 0;

  /**
   * Optional async wrapper around the background token-refresh XHR.
   *
   * When set, the wrapper is called with a `doRefresh` factory instead of
   * executing the HTTP call directly. This allows callers (e.g. the rehash MFE)
   * to wrap the XHR in an OpenTelemetry span — activating the span's context
   * before `doRefresh()` is invoked so that the auto-instrumented XHR becomes
   * a child of the wrapper span. The wrapper is responsible for ending the span
   * regardless of success or failure.
   *
   * Only applies to mid-session background token refreshes (`refreshToken()`).
   * The initial sign-in call (`signin()`) is NOT wrapped.
   *
   * @internal Set via {@link setRefreshHook}.
   */
  private refreshHook: ((doRefresh: () => Promise<unknown>) => Promise<unknown>) | null = null;

  constructor() {
    this.config = inject(ConfigService);
    this.init();
  }

  private init() {
    const user = sessionStorage.getItem(this.USER_KEY);
    const expiry = sessionStorage.getItem(this.EXPIRY_STORAGE_KEY);
    const refreshToken = sessionStorage.getItem(this.REFRESH_STORAGE_KEY);
    const token = sessionStorage.getItem(this.AUTH_STORAGE_KEY);
    const cognitoToken = sessionStorage.getItem(this.AUTH_COGNITO_KEY);

    this.setToken(token ? token : undefined);
    this._cognitoToken$.next(cognitoToken ? cognitoToken : undefined);
    this._expiry$.next(expiry ? +expiry : undefined);
    this._refreshToken$.next(refreshToken ? refreshToken : undefined);

    try {
      const jwtHelper = new JwtHelperService();
      const tokenPayload = jwtHelper.decodeToken(token || '');
      const restoredUser = User.fromToken(tokenPayload, JSON.parse(user || '').dealerno);
      const storedSsoProvider = sessionStorage.getItem(this.SSO_PROVIDER_STORAGE_KEY) as SsoProvider | null;
      if (storedSsoProvider && !restoredUser.isInternal) {
        restoredUser.ssoProvider = storedSsoProvider;
      }
      this._user$.next(restoredUser);
    } catch {
      this._user$.next(undefined);
    }

    if (refreshToken && expiry && refreshToken.length > 0) {
      this.setRefreshTimeout(+expiry);
    }
  }

  getUser() {
    return this._user$.value;
  }

  /**
   * Registers (or clears) an async wrapper around the background token-refresh
   * XHR triggered by the internal `setRefreshTimeout` timer.
   *
   * Pass a function to wrap the refresh in an OTel span (or other
   * instrumentation). Pass `null` to remove the hook, which should be done in
   * `ngOnDestroy` to prevent stale closures from running after the component is
   * destroyed.
   *
   * @example
   * ```ts
   * // In ngOnInit:
   * this.authService.setRefreshHook(async (doRefresh) => {
   *   const span = this.telemetry.startAuthRefreshSpan(this.appId, this.user?.dealerno ?? 0);
   *   return context.with(trace.setSpan(context.active(), span), () =>
   *     doRefresh().finally(() => span.end())
   *   );
   * });
   *
   * // In ngOnDestroy:
   * this.authService.setRefreshHook(null);
   * ```
   *
   * @param hook An async wrapper function, or `null` to clear the hook.
   */
  setRefreshHook(hook: ((doRefresh: () => Promise<unknown>) => Promise<unknown>) | null): void {
    this.refreshHook = hook;
  }

  isAuthenticated() {
    return this._user$.getValue() && this._token$.getValue() && (this._expiry$.getValue() || 0) > new Date().getTime();
  }

  private setToken(accessToken: string | undefined) {
    this.setSimpleCookie('ACADV-WebSocket-Auth', accessToken);
    this._token$.next(accessToken);
  }

  private setSimpleCookie(name: string, value: string | undefined) {
    // Local HTTP dev cannot send Secure cookies to ws://localhost:9000; use Lax without Secure.
    const cookieFlags = this.config.isLocalDev
      ? 'SameSite=Lax'
      : 'Domain=.acacceptance.com; SameSite=None; Secure';
    document.cookie = `${name}=${value || ''}; Path=/; ${cookieFlags}${
      value ? '' : '; Expires=Thu, 01 Jan 1970 00:00:01 GMT'
    }`;
  }

  getToken() {
    return this._token$.value;
  }

  getRedirectTo() {
    return localStorage.getItem(this.REDIRECT_STORAGE_KEY) || '';
  }

  setConnectionId(connectionId: string) {
    this._connectionId = connectionId;
  }

  getConnectionId() {
    return this._connectionId;
  }

  setRedirectTo(redirectTo?: string) {
    if (!redirectTo) {
      localStorage.removeItem(this.REDIRECT_STORAGE_KEY);
    } else {
      localStorage.setItem(this.REDIRECT_STORAGE_KEY, redirectTo);
    }
  }

  signin(authorizationCode: string, local: boolean, parallel: boolean) {
    return this.http.post<any>(`${this.config.authApiUrl}/getOAuth2Token`, { authorization_code: authorizationCode, local: local, parallel: parallel });
  }

  registerSignIn() {
    return this.http.post<any>(`${this.config.sharedApiUrl}/event/signin`, {});
  }

  log(module: string, action: string, result?: string, data?: any) {
    return this.http.post<any>(`${this.config.sharedApiUrl}/event/log`, {
      eventModule: module,
      eventAction: action,
      eventResult: result,
      eventDataJson: data ? JSON.stringify(data) : null,
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }

  signOut(preventPersist?: boolean) {
    this._beforeSignOut$.next(true);

    setTimeout(() => {
      this.clear();
      window.location.href = this.config.authUrl + (preventPersist ? '' : '?to=' + window.location.pathname);
    });
  }

  setAuth(model: AuthorizationModel): void {
    this.setToken(model.authorizationToken);
    this._expiry$.next(model.expiration);
    this._cognitoToken$.next(model.cognitoToken);
    this._refreshToken$.next(model.refresh_token);
    sessionStorage.setItem(this.AUTH_STORAGE_KEY, model.authorizationToken);
    sessionStorage.setItem(this.EXPIRY_STORAGE_KEY, model.expiration + '');
    sessionStorage.setItem(this.AUTH_COGNITO_KEY, model.cognitoToken);

    if (model.refresh_token?.length > 0) {
      this._refreshToken$.next(model.refresh_token);
      sessionStorage.setItem(this.REFRESH_STORAGE_KEY, model.refresh_token);
      this.setRefreshTimeout(model.expiration);
    }
  }

  setUser(user: User) {
    this._user$.next(user);
    sessionStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.setSsoProvider(user.ssoProvider);
  }

  setSsoProvider(ssoProvider?: SsoProvider) {
    if (!ssoProvider) {
      sessionStorage.removeItem(this.SSO_PROVIDER_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(this.SSO_PROVIDER_STORAGE_KEY, ssoProvider);
  }

  getSsoProvider() {
    return (sessionStorage.getItem(this.SSO_PROVIDER_STORAGE_KEY) as SsoProvider | null) || '';
  }

  /**
   * Sets the dealer number for the current user.
   * Only internal users (ACA employees) are allowed to switch dealers.
   * External dealer users are restricted to their assigned dealer.
   * 
   * @param dealerNumber - The dealer number to switch to
   * @param subsequentRouteChange - Whether this change will be followed by a route change
   */
  setDealer(dealerNumber: number, subsequentRouteChange = false) {
    const user = this._user$.getValue();
    
    // Security: Only internal users can switch dealers
    if (!user?.isInternal) {
      console.warn('[AuthService] Dealer switching is only allowed for internal users');
      return;
    }
    
    if (user && user.dealerno != dealerNumber) {
      user.dealerno = dealerNumber;
      this.setUser(user);
      this.dealerChanged$.next({ dealerNumber, subsequentRouteChange });
    }
  }

  clear(): void {
    this.setToken(undefined);
    this._cognitoToken$.next(undefined);
    this._expiry$.next(undefined);
    this._user$.next(undefined);
    this._refreshToken$.next(undefined);
    sessionStorage.removeItem(this.AUTH_STORAGE_KEY);
    sessionStorage.removeItem(this.EXPIRY_STORAGE_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.SSO_PROVIDER_STORAGE_KEY);
    sessionStorage.removeItem(this.AUTH_COGNITO_KEY);
    sessionStorage.removeItem(this.REFRESH_STORAGE_KEY);

    clearTimeout(this.refreshTokenTimer);
    this.refreshTokenTimer = undefined;
  }

  /*
    NOTE: refresh tokens can be implemented a variety of ways:
      1) Client side via timer (easiest to test; which is what this uses)
      2) Client side via http interceptor (listen for 401/Unauthorized response, refresh the token, then retry request)
      3) Server side middleware (requires cache provider; catch on server, refresh token, set header, and retry)
  */
  private setRefreshTimeout(newExpiry: number) {
    const expirationTimeInMilliseconds = newExpiry - Date.now();
    const refreshAtMilliseconds = expirationTimeInMilliseconds - expirationTimeInMilliseconds * AuthService.REFRESH_TOKEN_BEFORE_EXPIRATION_PCT;

    clearTimeout(this.refreshTokenTimer);

    this.refreshTokenTimer = setTimeout(async () => {
      const refreshToken = this._refreshToken$.value;
      if (refreshToken && refreshToken?.length > 0) {
        const response = await this.refreshToken(refreshToken);
        if (response && this._expiry$.value) {
          this.setRefreshTimeout(this._expiry$.value);
        }
      }
    }, refreshAtMilliseconds);
  }

  private async refreshToken(refreshToken: string): Promise<unknown> {
    // Extract the HTTP call into a named factory so it can be passed to
    // an optional hook (e.g. the rehash MFE's OTel span wrapper) or called
    // directly when no hook is registered.
    const doRefresh = (): Promise<RefreshTokenResponseModel> =>
      firstValueFrom(
        this.http
          .post<RefreshTokenResponseModel>(`${this.config.authApiUrl}/getOAuth2Token`, {
            refresh_token: refreshToken,
            local: this.isFromLocal,
            parallel: this.isFromParallel
          })
          .pipe(
            tap((res) => {
              const model = {
                authorizationToken: res.body.id_token,
                cognitoToken: res.body.access_token,
                refresh_token: this._refreshToken$.getValue() || '',
                expiration: res.body.expires_in * 1000 + Date.now()
              } as AuthorizationModel;

              this.setAuth(model);
            })
          )
      );

    // When a hook is registered (e.g. to wrap with an OTel span), delegate to it
    // so the XHR is made inside the hook's active context. Otherwise call directly.
    return this.refreshHook ? this.refreshHook(doRefresh) : doRefresh();
  }
}
